import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { removeToast } from '~actions';
import CloseIcon from '~Assets/icons/close.svg';
import { useDeviceInfo, useSelector } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import InfoIcon from '~Assets/icons/info.svg';

const defaultStyling = css`
  padding: 12px 40px 12px 16px;
  min-height: 56px;
  width: 312px;
`;

// Phone-specific styling
const phoneStyling = css`
  padding: 8px 12px;
  min-height: 48px;
  min-width: 180px;
  margin: 0 auto;
  max-width: 320px;
`;

const Message = styled.div<{ isError: boolean; isPhone?: boolean }>`
  position: relative;
  border-radius: 4px;
  box-shadow: 0px 0px 16px 0px rgb(0 0 0 / 24%), 0px 1px 3px 0px rgb(0 0 0 / 50%);
  background-color: ${({ isError }) => (isError ? '#ffffff' : '#ffe6b4')};
  margin-bottom: 8px;
  box-sizing: border-box;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
  ${({ isPhone }) => (isPhone ? phoneStyling : defaultStyling)}
  & > .SVG-icon {
    display: inline-block;
    margin-right: 12px;
    background-color: var(--colorBlueGray900);
  }
`;

const Text = styled.p<{ isError: boolean }>`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 16px;
  color: ${({ isError }) => (isError ? '#e3273b' : '#1c232c')};
  margin: 0;
`;

const ActionLabel = styled.span`
  display: block;
  font-size: 12px;
  color: #333;
  margin-top: 4px;
`;

const CloseIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  mask-image: url(${CloseIcon});
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: #545960;
  position: absolute;
  right: 8px;
  top: 8px;
`;

type ToastProps = {
  id: string;
  message: string;
  autoRemove: boolean;
  isError: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
};

const Toast = ({ id, message, autoRemove, isError, action }: ToastProps) => {
  const toasts = useSelector((state) => state.toasts);

  // Joar: I decided to use a "ref" here instead of state because I'm not sure
  //       if state will actually be reflected in the timeout/effect context
  const isRemoving = useRef(false);
  const deviceInfo = useDeviceInfo();

  const dispatch = useDispatch();

  // Remove old toast with the same message
  useEffect(() => {
    const sameMessageToasts = toasts.filter((toast) => toast.message === message);

    // Remove all exept the last one
    sameMessageToasts.slice(0, -1).forEach((toast) => {
      dispatch(removeToast(toast.id));
    });
  }, [toasts]);

  // Get the timestamp of the most recent user action
  const time = useSelector((state) => state.timeOfLastAction);

  useEffect(() => {
    if (!autoRemove) {
      return;
    }

    // If we've already marked this toast for removal, do nothing
    if (isRemoving.current) {
      return;
    }

    const isActive = moment(time).isAfter(moment().subtract(10, 'seconds'));

    // If the user hasnt made an action in 10 seconds, dont hide it.
    // We want to ensure someone actually has seen it
    if (!isActive) {
      return;
    }

    // Remove the toast
    setTimeout(() => {
      dispatch(removeToast(id));
    }, 3000);

    isRemoving.current = true;
  }, [time]);

  const handleToastClick = () => {
    if (action?.onClick) {
      action.onClick();
    }

    dispatch(removeToast(id));
  };

  return (
    <Message key={id} isError={isError} isPhone={deviceInfo?.isPhone}>
      {!isError && (
        <Icon
          src={InfoIcon}
          style={{
            flexShrink: 0,
          }}
        />
      )}
      <Text isError={isError} onTouchStart={handleToastClick}>
        {message}
        {action?.label && <ActionLabel>{action.label}</ActionLabel>}
      </Text>
      {!autoRemove && <CloseIconWrapper onTouchStart={() => dispatch(removeToast(id))} />}
    </Message>
  );
};

export default Toast;
